/**
 * @file cursor-global.tsx
 * @summary
 * Componente global para renderizar um cursor customizado.
 *
 * @remarks
 * - Para colaboração em tempo real.
 *
 * @example
 * ```tsx
 * import { GlobalCursor } from "@nucleo/visuais/kibo-ui/cursor/cursor.globals";
 *
 * <GlobalCursor />;
 * ```
 */
"use client";

import React, { useEffect, useRef, useCallback, ReactNode } from "react";
import { format, isValid } from "date-fns";
import { Cursor, CursorBody, CursorName, CursorMessage } from "./cursor";
import { BG_CLASSES, BORDER_CLASSES, TEXT_CLASSES } from "./cores";
import { useCursor } from "./contexto";
import { ColorKey } from "./tipos";
import { cn } from "visuais/fmt";

export interface CursorDisplayData {
  name?: string;
  customBody?: ReactNode | ((data: CursorDisplayData) => ReactNode);
  background?: string;
  [key: string]: any;
}

/**
 * Renderiza o cursor customizado globalmente.
 */
export const GlobalCursor = () => {
  const { hoveredData } = useCursor<CursorDisplayData>();
  const cursorRef = useRef<HTMLDivElement>(null);
  const latestMousePos = useRef({ x: 0, y: 0 });

  const animateCursor = useCallback(() => {
    if (cursorRef.current) {
      cursorRef.current.style.transform = `translate3d(${latestMousePos.current.x}px, ${latestMousePos.current.y}px, 0)`;
    }
  }, []);

  useEffect(() => {
    const updateMousePosition = (e: MouseEvent) => {
      latestMousePos.current = { x: e.clientX, y: e.clientY };
      requestAnimationFrame(animateCursor);
    };
    window.addEventListener("mousemove", updateMousePosition);
    return () => {
      window.removeEventListener("mousemove", updateMousePosition);
      document.body.style.cursor = "default";
    };
  }, [animateCursor]);

  const renderDetails = () => {
    if (!hoveredData) return null;
    if (typeof hoveredData.customBody === "function") {
      return hoveredData.customBody(hoveredData);
    }
    if (hoveredData.customBody) {
      return hoveredData.customBody;
    }
    return (
      <CursorMessage className="text-xs">
        {Object.entries(hoveredData).map(([key, value]) => {
          if (["name", "customBody", "background"].includes(key)) return null;
          let displayValue: any = value;
          if (value instanceof Date && isValid(value)) {
            displayValue = format(value, "dd/MM/yyyy HH:mm");
          } else if (typeof value === "string") {
            const d = new Date(value);
            if (isValid(d)) displayValue = format(d, "dd/MM/yyyy HH:mm");
          } else if (value && typeof value === "object" && "name" in value) {
            displayValue = (value as { name: string }).name;
          }
          const displayKey = key.charAt(0).toUpperCase() + key.slice(1);
          return (
            <p key={key} className="mb-1">
              {displayKey}: <span className="font-medium">{displayValue}</span>
            </p>
          );
        })}
      </CursorMessage>
    );
  };

  const colorKey = (hoveredData?.background as ColorKey) ?? "amber";
  const bgClass = BG_CLASSES[colorKey];
  const border = BORDER_CLASSES[colorKey];
  const textClass = TEXT_CLASSES[colorKey];
  return (
    <Cursor
      ref={cursorRef}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {hoveredData && (
        <CursorBody /*  ⬅ override do contraste aqui */
          className={cn(
            "p-3 border shadow-md transition-opacity duration-300 w-fit max-w-lg absolute",
            bgClass,
            border,
            textClass
          )}
        >
          {hoveredData.name && (
            <CursorName className={cn("font-semibold text-lg", textClass)}>
              {hoveredData.name}
            </CursorName>
          )}
          {renderDetails()}
        </CursorBody>
      )}
    </Cursor>
  );
};
